import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "lifecycle-extension-in-android"
    }}>{`Lifecycle extension in Android`}</h1>
    <h2 {...{
      "id": "implementing-lifecycle-data-collection-in-android"
    }}>{`Implementing Lifecycle data collection in Android`}</h2>
    <p>{`For implementation details, please reference the `}<a parentName="p" {...{
        "href": "/client-sdks/src/pages/home/base/mobile-core/lifecycle/index.md"
      }}>{`implementation guide for Lifecycle`}</a>{`.`}</p>
    <h2 {...{
      "id": "tracking-app-crashes-in-android"
    }}>{`Tracking app crashes in Android`}</h2>
    <p>{`This information helps you understand how crashes are tracked and the best practices to handle false crashes.`}</p>
    <h3 {...{
      "id": "how-does-crash-reporting-work"
    }}>{`How does crash reporting work?`}</h3>
    <p>{`When Lifecycle data collection is implemented in an application, pausing Lifecycle data collection sets a flag which is persisted in the application. When the application is launched again and Lifecycle data collection is started, if the flag is `}<strong parentName="p">{`not`}</strong>{` set then a crash event is reported.`}</p>
    <p>{`The flag is controlled by calls to `}<a parentName="p" {...{
        "href": "/client-sdks/src/pages/home/base/mobile-core/lifecycle/api-reference.md#lifecyclepause"
      }}>{`lifecyclePause`}</a>{` (which sets the flag) and `}<a parentName="p" {...{
        "href": "/client-sdks/src/pages/home/base/mobile-core/lifecycle/api-reference.md#lifecyclestart"
      }}>{`lifecycleStart`}</a>{` (which clears the flag).`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`To ensure accurate session and crash reporting, you should call `}<inlineCode parentName="p">{`MobileCore.lifecycleStart()`}</inlineCode>{` in the `}<strong parentName="p">{`onResume()`}</strong>{` method of each activity, and `}<inlineCode parentName="p">{`MobileCore.lifecyclePause()`}</inlineCode>{` in the `}<strong parentName="p">{`onPause()`}</strong>{` method of each activity.`}<br /><br />{`You should not call Lifecycle start and pause APIs from fragments.`}</p>
    <p>{`To understand why this is essential, here is an illustration of the Android activity lifecycle:`}<span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "652px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "108.74999999999999%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/e30dc92032a4fa0afec3e601408bb0ba/5530d/android-crash.webp 320w", "/client-sdks/static/e30dc92032a4fa0afec3e601408bb0ba/0c8fb/android-crash.webp 640w", "/client-sdks/static/e30dc92032a4fa0afec3e601408bb0ba/9a7b5/android-crash.webp 652w"],
            "sizes": "(max-width: 652px) 100vw, 652px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/e30dc92032a4fa0afec3e601408bb0ba/dd4a7/android-crash.png 320w", "/client-sdks/static/e30dc92032a4fa0afec3e601408bb0ba/0f09e/android-crash.png 640w", "/client-sdks/static/e30dc92032a4fa0afec3e601408bb0ba/43b96/android-crash.png 652w"],
            "sizes": "(max-width: 652px) 100vw, 652px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/client-sdks/static/e30dc92032a4fa0afec3e601408bb0ba/43b96/android-crash.png",
            "alt": "android crash",
            "title": "android crash",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`For more information about the Android activity lifecycle, see `}<a parentName="p" {...{
        "href": "https://developer.android.com/guide/components/activities/"
      }}>{`Activities`}</a>{`.`}</p>
    <p>{`This Android lifecycle illustration was created and shared by the `}<a parentName="p" {...{
        "href": "https://source.android.com/"
      }}>{`Android Open Source Project`}</a>{` and used according to terms in the `}<a parentName="p" {...{
        "href": "https://creativecommons.org/licenses/by/2.5/"
      }}>{`Creative Commons 2.5 Attribution License`}</a>{`.`}</p>
    <h3 {...{
      "id": "what-can-cause-a-false-crash-to-be-reported"
    }}>{`What can cause a false crash to be reported?`}</h3>
    <ul>
      <li parentName="ul">{`If you are debugging by using an IDE such as Android Studio, and launching the app again from the IDE while the app is in the foreground causes a crash.`}
        <ul parentName="li">
          <li parentName="ul">{`You can avoid this crash by backgrounding the app before launching again from the IDE.`}</li>
        </ul>
      </li>
      <li parentName="ul">{`If the previous foreground Activity of your app is moved to the background and does not call `}<inlineCode parentName="li">{`MobileCore.lifecyclePause()`}</inlineCode>{`in `}<inlineCode parentName="li">{`onPause`}</inlineCode>{`, and your app is manually closed or killed by the operating system, the next launch results in a crash.`}</li>
    </ul>
    <h3 {...{
      "id": "how-should-fragments-be-handled"
    }}>{`How should Fragments be handled?`}</h3>
    <p>{`Fragments have application lifecycle events that are similar to Activities. However, a Fragment cannot be active without being attached to an Activity. Therefore, you should implement the Lifecycle APIs from Activities.`}</p>
    <h2 {...{
      "id": "further-reading"
    }}>{`Further reading`}</h2>
    <p>{`The following guides further illustrate the expected Lifecycle scenarios along with example unexpected scenarios and how to correct them.`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/client-sdks/src/pages/home/base/mobile-core/lifecycle/behavior-reference.md"
        }}>{`Lifecycle behavior reference`}</a>{` when sending Lifecycle events to Analytics.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/client-sdks/src/pages/edge/lifecycle-for-edge-network/behavior-reference.md"
        }}>{`Lifecycle behavior reference`}</a>{` when sending Lifecycle events to Edge Network.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      